import classNames from "classnames";
import Icon from "components/Common/Icon";
import { MediaQuery } from "@outplayed/responsive";
import { getWowStatic } from "hooks/wow/getStaticData";
import { formatEnchantName, getEnchantImgSrc, getEnchantQualityIconSrc } from "lib/enchant-display";
import EnchantTooltip from "../Tooltips/EnchantTooltip";
import { getFormattedContentType, getFormattedNames, getIsHealer } from "lib/wowHelpers";
import React, { useState } from "react";
import SubtitleAndDescription from "../Common/SubtitleAndDescription";
import WowTooltip from "../Tooltips/WowTooltip";
import ArmorItemCombo from "./ArmorItemCombo";
import CraftedIcons from "./CraftedIcons";
import GemIcons from "./GemIcons";
import WeaponItem from "./WeaponItem";

const PvEGearGuide = (props) => {
  const { classType, specType, encounterType } = props;
  const { displayClassName, displaySpecName } = getFormattedNames(classType, specType);
  // bis = Best In Slot, pop = Most Popular
  const [stat, setStat] = useState("pop");

  const specClassName = `${displaySpecName} ${displayClassName}`;
  const contentType = getFormattedContentType(encounterType);

  return (
    <div className="wow-items-section content-section">
      <div className="header-section">
        <div className="title-and-button">
          <SubtitleAndDescription subtitle={`${stat === "bis" ? "BIS" : "Most Popular"} ${specClassName} Gear`} />
          {/* <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE" renderNullOnFail>
            <ToggleButton stat={stat} setStat={setStat} />
          </MediaQuery> */}
        </div>
        <div className="text-blurb">
          The {stat === "bis" ? "Best in Slot" : "Most Popular"} Gear used by the best {specClassName} players. Gear in each slot
          is determined by analyzing the top {specClassName} {contentType} rankings. The selected piece of gear is the{" "}
          {stat === "bis" ? "most performant" : "most popular"} item for that slot.{" "}
          {stat === "bis"
            ? "For tier piece slots, we've added a 10% popularity threshold in order to guarantee that at least 4/5 of the best tier set armor pieces are recommended."
            : ""}
        </div>
        {/* <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
          <ToggleButton stat={stat} setStat={setStat} />
        </MediaQuery> */}
      </div>
      <WOWArmorLayoutContainer {...props} stat={stat} />
      <WOWWeaponLayoutContainer {...props} stat={stat} />
    </div>
  );
};

const ToggleButton = (props) => {
  const { stat, setStat } = props;

  return (
    <div className="toggle-button-container">
      <div
        className={classNames("toggle-button left", {
          active: stat === "pop",
        })}
        onClick={() => setStat("pop")}
      >
        Most Popular
      </div>
      <div
        className={classNames("toggle-button right", {
          active: stat === "bis",
        })}
        onClick={() => setStat("bis")}
      >
        Best in Slot
      </div>
    </div>
  );
};

const getItemType = (stat, specType) =>
  stat === "bis" && getIsHealer(specType) ? "hps_item" : stat === "bis" ? "dps_item" : "popularity_item";

// Combo entries from newer aggregator runs carry their own variant data
// (bonus ids, gems, enchant id, crafted quality). Older published JSON does
// not, so fall back to borrowing from the builds items block when the combo
// item is the same item the slot recommends.
const getSlotVariant = (builds, itemType, slot, itemId) => {
  const slotItem = builds?.[slot]?.[itemType];
  if (!slotItem || slotItem.item_id !== itemId) return {};
  return slotItem;
};

const enrichComboEntry = (builds, itemType, entry, firstSlot, secondSlot) => {
  if (!entry) return entry;

  const first = getSlotVariant(builds, itemType, firstSlot, entry.first_item_id);
  const second = getSlotVariant(builds, itemType, secondSlot, entry.second_item_id);

  return {
    ...entry,
    first_item_bonus_ids: entry.first_item_bonus_ids || first.bonus_ids,
    first_item_crafted_quality: entry.first_item_crafted_quality || first.crafted_quality,
    first_item_gems: entry.first_item_gems || first.gems,
    first_item_enchant: entry.first_item_enchant || first.enchant_id,
    first_item_enchant_item_id: entry.first_item_enchant_item_id || first.enchant_item_id,
    first_item_enchant_display_name: entry.first_item_enchant_display_name || first.enchant_display_name,
    first_item_enchant_quality: entry.first_item_enchant_quality || first.enchant_quality,
    first_item_embellishment_item_ids: entry.first_item_embellishment_item_ids || first.embellishment_item_ids,
    first_item_missive_item_id: entry.first_item_missive_item_id || first.missive_item_id,
    second_item_bonus_ids: entry.second_item_bonus_ids || second.bonus_ids,
    second_item_crafted_quality: entry.second_item_crafted_quality || second.crafted_quality,
    second_item_gems: entry.second_item_gems || second.gems,
    second_item_enchant: entry.second_item_enchant || second.enchant_id,
    second_item_enchant_item_id: entry.second_item_enchant_item_id || second.enchant_item_id,
    second_item_enchant_display_name: entry.second_item_enchant_display_name || second.enchant_display_name,
    second_item_enchant_quality: entry.second_item_enchant_quality || second.enchant_quality,
    second_item_embellishment_item_ids: entry.second_item_embellishment_item_ids || second.embellishment_item_ids,
    second_item_missive_item_id: entry.second_item_missive_item_id || second.missive_item_id,
  };
};

const WOWArmorLayoutContainer = (props) => {
  const firstColumnItems = ["head", "neck", "shoulder", "cape", "chest", "shirt", "tabard", "wrist"];
  const secondColumnItems = ["gloves", "belt", "legs", "feet"];
  const comboItems = ["ring1_ring2", "trinket1_trinket2"];

  return (
    <div className="armor-layout-container">
      <WOWArmorLayoutColumn {...props} itemSlots={firstColumnItems} isLeftSide />
      <WOWArmorLayoutColumn {...props} itemSlots={secondColumnItems} comboItems={comboItems} />
    </div>
  );
};

const WOWArmorLayoutColumn = (props) => {
  const { builds, combos, itemSlots, isLeftSide, stat, specType, comboItems, encounterType } = props;

  const itemType = getItemType(stat, specType);

  const setPieces = builds
    ? Object.values(builds)
        .map((slotData) => slotData?.[itemType]?.item_id)
        .filter((itemId) => itemId > 0)
        .join(":")
    : "";

  const itemData = itemSlots.map((slot) => {
    if (!builds) {
      return {
        slot,
      };
    }

    const allItemData = builds[slot];
    const itemInSlot = allItemData?.[itemType];

    if (itemInSlot) {
      const id = itemInSlot.item_id;
      return {
        id: id,
        quality: itemInSlot.quality,
        slot,
        popPerc: parseFloat(itemInSlot.perc).toFixed(1) + "%",
        popCount: itemInSlot.count,
        enchant: itemInSlot.enchant,
        enchantId: itemInSlot.enchant_id,
        enchantItemId: itemInSlot.enchant_item_id,
        enchantDisplayName: itemInSlot.enchant_display_name,
        enchantQuality: itemInSlot.enchant_quality,
        bonusIds: itemInSlot.bonus_ids,
        embellishmentItemIds: itemInSlot.embellishment_item_ids,
        missiveItemId: itemInSlot.missive_item_id,
        craftingQuality: itemInSlot.crafted_quality,
        gems: itemInSlot.gems,
      };
    }

    return {
      slot,
    };
  });

  return (
    <div className="armor-layout">
      {itemData.map((item, index) => {
        return (
          <ArmorItem
            key={index}
            id={item.id}
            isLeftSide={isLeftSide}
            // itemColor={getItemQualityColor(item.quality)}
            itemColor={item.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
            popPerc={item.popPerc}
            popCount={item.popCount}
            enchantName={item.enchant}
            enchantId={item.enchantId}
            enchantItemId={item.enchantItemId}
            enchantDisplayName={item.enchantDisplayName}
            enchantQuality={item.enchantQuality}
            bonusIds={item.bonusIds}
            embellishmentItemIds={item.embellishmentItemIds}
            missiveItemId={item.missiveItemId}
            craftingQuality={item.craftingQuality}
            setPieces={setPieces}
            gems={item.gems}
            slot={item.slot}
          />
        );
      })}
      {comboItems?.map((item, index) => {
        const [firstSlot, secondSlot] = item.split("_");
        const entry = enrichComboEntry(builds, itemType, combos?.[item]?.[itemType]?.[0], firstSlot, secondSlot);
        return <ArmorItemCombo key={index} data={entry} encounterType={encounterType} />;
      })}
    </div>
  );
};

const ArmorItem = (props) => {
  const {
    isLeftSide,
    id,
    popPerc,
    popCount,
    enchantName,
    enchantId,
    enchantItemId,
    enchantDisplayName,
    enchantQuality,
    slot,
    bonusIds,
    embellishmentItemIds,
    missiveItemId,
    craftingQuality,
    setPieces,
    gems,
  } = props;
  let { itemName = "Cosmetic", itemColor, className } = props;
  let img;

  const staticData = getWowStatic("item", id);
  if (!staticData || !staticData.name) {
    if (slot === "shirt" || slot === "tabard") {
      img = `https://static.bigbrain.gg/wow/icons/empty-slots/${slot}.png`;
    } else {
      itemName = "";
    }

    itemColor = "#717171";
    className = "empty-slot";
  } else {
    itemName = staticData.name;
    img = staticData.img;
  }

  return (
    <div className={isLeftSide ? `armor-item ${className}` : `armor-item-reversed ${className}`} item-id={id}>
      <div className="armor-item-container">
        <WowTooltip
          type="item"
          id={id}
          bonusIds={bonusIds}
          craftingQuality={craftingQuality}
          pcs={setPieces}
          enchant={enchantId}
          gems={gems}
        >
          <Icon staticDataImg={img} color={itemColor} classType="bis-item-icon" />
        </WowTooltip>
        <div className={isLeftSide ? "item-info" : "item-info-right"}>
          <div className="item-name" style={{ color: itemColor }}>
            {itemName}
          </div>
          <div className="enchant-info">
            <GemIcons gems={gems} />
            <CraftedIcons embellishmentItemIds={embellishmentItemIds} missiveItemId={missiveItemId} />
            {enchantName ? (
              <EnchantTooltip itemId={enchantItemId}>
                <Icon iconSize="18px" staticDataImg={getEnchantImgSrc(enchantItemId)} />
              </EnchantTooltip>
            ) : null}
            <div className="enchant-text">{formatEnchantName(enchantName, enchantDisplayName)}</div>
            {enchantName ? (
              <img
                style={
                  enchantQuality == 2
                    ? { width: "16px", height: "8px", marginLeft: "4px" }
                    : { width: "16px", height: "12px", marginLeft: "4px" }
                }
                src={getEnchantQualityIconSrc(enchantQuality)}
              ></img>
            ) : null}
          </div>
        </div>
      </div>
      {itemName !== "Cosmetic" && (
        <div className="popularity-container">
          <div className="popularity-value">{popPerc}</div>
          <div className="popularity-label">({popCount?.toLocaleString("en-US")} parses)</div>
        </div>
      )}
    </div>
  );
};

const WOWWeaponLayoutContainer = (props) => {
  const { builds, combos, stat, specType } = props;
  const itemType = getItemType(stat, specType);
  const weapons = enrichComboEntry(builds, itemType, combos?.weapon1_weapon2?.[itemType]?.[0], "weapon1", "weapon2");

  const weapon1Data = getWowStatic("item", weapons?.first_item_id);
  const weapon2Data = getWowStatic("item", weapons?.second_item_id);

  const weapon1ItemColor = weapons?.quality1 === 5 ? "#ff8000" : "rgb(163, 53, 238)";
  const weapon2ItemColor = weapons?.quality2 === 5 ? "#ff8000" : "rgb(163, 53, 238)";

  if (!weapon2Data) {
    return (
      <>
        <div className="weapon-combos-label">Weapon: Main Hand</div>
        <div className="weapon-layout-container">
          <div className="weapons">
            <WeaponItem
              positionLeft
              itemName={weapon1Data?.name}
              // itemColor={getItemQualityColor(weaponSlot1.quality)}
              itemColor={weapon1ItemColor}
              img={weapon1Data?.img}
              id={weapons?.first_item_id}
              enchantName={weapons?.first_item_enchant_name}
              enchantId={weapons?.first_item_enchant}
              enchantItemId={weapons?.first_item_enchant_item_id}
              enchantDisplayName={weapons?.first_item_enchant_display_name}
              enchantQuality={weapons?.first_item_enchant_quality}
              bonusIds={weapons?.first_item_bonus_ids}
              embellishmentItemIds={weapons?.first_item_embellishment_item_ids}
              missiveItemId={weapons?.first_item_missive_item_id}
              craftingQuality={weapons?.first_item_crafted_quality}
              gems={weapons?.first_item_gems}
              popularity={weapons?.popularity}
              count={weapons?.count}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="weapon-combos-label">Weapons: Main Hand & Off Hand</div>
      <div className="weapon-layout-container weapon-combos">
        <div className="weapons">
          <WeaponItem
            positionLeft
            itemName={weapon1Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapon1ItemColor}
            img={weapon1Data?.img}
            id={weapons?.first_item_id}
            enchantName={weapons?.first_item_enchant_name}
            enchantId={weapons?.first_item_enchant}
            enchantItemId={weapons?.first_item_enchant_item_id}
            enchantDisplayName={weapons?.first_item_enchant_display_name}
            enchantQuality={weapons?.first_item_enchant_quality}
            bonusIds={weapons?.first_item_bonus_ids}
            embellishmentItemIds={weapons?.first_item_embellishment_item_ids}
            missiveItemId={weapons?.first_item_missive_item_id}
            craftingQuality={weapons?.first_item_crafted_quality}
            gems={weapons?.first_item_gems}
          />
          <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE" renderNullOnFail>
            <div className="popularity-container">
              <div className="popularity-value">{parseFloat(weapons?.popularity).toFixed(1)}%</div>
              <div className="popularity-label">({weapons?.count.toLocaleString("en-US")} parses)</div>
            </div>
          </MediaQuery>
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
            <div className="horizontal-line" />
          </MediaQuery>
          <WeaponItem
            itemName={weapon2Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapon2ItemColor}
            img={weapon2Data?.img}
            id={weapons?.second_item_id}
            enchantName={weapons?.second_item_enchant_name}
            enchantId={weapons?.second_item_enchant}
            enchantItemId={weapons?.second_item_enchant_item_id}
            enchantDisplayName={weapons?.second_item_enchant_display_name}
            enchantQuality={weapons?.second_item_enchant_quality}
            bonusIds={weapons?.second_item_bonus_ids}
            embellishmentItemIds={weapons?.second_item_embellishment_item_ids}
            missiveItemId={weapons?.second_item_missive_item_id}
            craftingQuality={weapons?.second_item_crafted_quality}
            gems={weapons?.second_item_gems}
          />
        </div>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
          <div className="popularity-container">
            <div className="popularity-value">{parseFloat(weapons?.popularity).toFixed(1)}%</div>
            <div className="popularity-label">({weapons?.count.toLocaleString("en-US")} parses)</div>
          </div>
        </MediaQuery>
      </div>
    </>
  );
};

export default PvEGearGuide;
